import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { SurveyTemplate, SurveyTemplateState } from '@/types/survey.types';

const initialState: SurveyTemplateState = {
  templateList: [],
  reorderList: [],
  templateTagsList: [],
  editionList: [],
  thumbnailList: [],
  total: 0,
  loading: false,
  reorderLoading: false,
  error: null,
  addTemplate: { loading: false, error: null },
  updateTemplate: { loading: false, error: null },
  deleteTemplate: { loading: false, error: null },
};

const surveysSlice = createSlice({
  name: 'surveys',
  initialState,
  reducers: {
    fetchTemplatesRequest(state, _action: PayloadAction<{ page: number; limit: number; search?: string; sortField?: string; sortOrder?: string; status?: string; category?: string }>) {
      state.loading = true; state.error = null;
    },
    fetchTemplatesSuccess(state, action: PayloadAction<{ list: SurveyTemplate[]; total: number }>) {
      state.loading = false; state.templateList = action.payload.list; state.total = action.payload.total;
    },
    fetchTemplatesFailure(state, action: PayloadAction<string>) {
      state.loading = false; state.error = action.payload;
    },

    fetchReorderListRequest(state) { state.reorderLoading = true; },
    fetchReorderListSuccess(state, action: PayloadAction<SurveyTemplate[]>) {
      state.reorderLoading = false; state.reorderList = action.payload;
    },
    fetchReorderListFailure(state, action: PayloadAction<string>) {
      state.reorderLoading = false; state.error = action.payload;
    },

    reorderTemplatesRequest(state, _action: PayloadAction<{ list: { _id: string; order: number }[] }>) {
      state.reorderLoading = true;
    },
    reorderTemplatesSuccess(state) { state.reorderLoading = false; },
    reorderTemplatesFailure(state, action: PayloadAction<string>) {
      state.reorderLoading = false; state.error = action.payload;
    },

    addTemplateRequest(state, _action: PayloadAction<Record<string, unknown>>) {
      state.addTemplate = { loading: true, error: null };
    },
    addTemplateSuccess(state) { state.addTemplate = { loading: false, error: null }; },
    addTemplateFailure(state, action: PayloadAction<string>) {
      state.addTemplate = { loading: false, error: action.payload };
    },

    updateTemplateRequest(state, _action: PayloadAction<Record<string, unknown>>) {
      state.updateTemplate = { loading: true, error: null };
    },
    updateTemplateSuccess(state) { state.updateTemplate = { loading: false, error: null }; },
    updateTemplateFailure(state, action: PayloadAction<string>) {
      state.updateTemplate = { loading: false, error: action.payload };
    },

    deleteTemplateRequest(state, _action: PayloadAction<string>) {
      state.deleteTemplate = { loading: true, error: null };
    },
    deleteTemplateSuccess(state, action: PayloadAction<string>) {
      state.deleteTemplate = { loading: false, error: null };
      state.templateList = state.templateList.filter((t) => t._id !== action.payload);
    },
    deleteTemplateFailure(state, action: PayloadAction<string>) {
      state.deleteTemplate = { loading: false, error: action.payload };
    },

    fetchTemplateTagsRequest() {},
    fetchTemplateTagsSuccess(state, action: PayloadAction<string[]>) { state.templateTagsList = action.payload; },
    fetchTemplateTagsFailure(state, action: PayloadAction<string>) {
      state.error = action.payload;
    },

    addTemplateTagRequest(_state, _action: PayloadAction<string>) {},
    addTemplateTagSuccess(state, action: PayloadAction<string>) { state.templateTagsList.push(action.payload); },
    addTemplateTagFailure(state, action: PayloadAction<string>) {
      state.error = action.payload;
    },

    fetchEditionListRequest() {},
    fetchEditionListSuccess(state, action: PayloadAction<string[]>) { state.editionList = action.payload; },
    fetchEditionListFailure(state, action: PayloadAction<string>) {
      state.error = action.payload;
    },

    fetchThumbnailListRequest() {},
    fetchThumbnailListSuccess(state, action: PayloadAction<string[]>) { state.thumbnailList = action.payload; },
    fetchThumbnailListFailure(state, action: PayloadAction<string>) {
      state.error = action.payload;
    },
  },
});

export const {
  fetchTemplatesRequest, fetchTemplatesSuccess, fetchTemplatesFailure,
  fetchReorderListRequest, fetchReorderListSuccess, fetchReorderListFailure,
  reorderTemplatesRequest, reorderTemplatesSuccess, reorderTemplatesFailure,
  addTemplateRequest, addTemplateSuccess, addTemplateFailure,
  updateTemplateRequest, updateTemplateSuccess, updateTemplateFailure,
  deleteTemplateRequest, deleteTemplateSuccess, deleteTemplateFailure,
  fetchTemplateTagsRequest, fetchTemplateTagsSuccess, fetchTemplateTagsFailure,
  addTemplateTagRequest, addTemplateTagSuccess, addTemplateTagFailure,
  fetchEditionListRequest, fetchEditionListSuccess, fetchEditionListFailure,
  fetchThumbnailListRequest, fetchThumbnailListSuccess, fetchThumbnailListFailure,
} = surveysSlice.actions;

export default surveysSlice.reducer;
